import { useCallback, useMemo, useRef, useState } from 'react'

import { Alert, AlertDescription } from '@/components/ui/alert'
import { Card } from '@/components/ui/card'
import { Spinner } from '@/components/ui/spinner'
import { useIsCareforceOrgAdmin } from '@/hooks/useIsCareforceOrgAdmin'
import { CareforceOrgNotice } from './CareforceOrgNotice'
import { PhoneLineGrid } from './PhoneLineGrid'
import { PhoneLineMap } from './PhoneLineMap'
import { PhoneLineTooltip, TOOLTIP_MIN_WIDTH } from './PhoneLineTooltip'
import { activeLinePlacement } from '../mapPlacement'
import { useAllocationBoardQuery } from '../hooks/useAllocationBoardQuery'

import { AllocationRibbon } from './AllocationRibbon'
import { AllocationTable } from './AllocationTable'
import {
  ALLOCATION_GRID_COLS,
  buildDutyColorMap,
  poolSplit,
  UNKNOWN_DUTY_COLOR,
} from '../constants'

type HoverState = { line: number; top: number; left: number }

/**
 * What the user sees when the board fails to load. Deliberately fixed and generic —
 * see the render site for why the underlying error text is withheld.
 */
const ALLOCATION_BOARD_ERROR =
  "Couldn't load Active Lines. Retrying automatically."

/** Vertical gap between the hovered mark and the tooltip, in px. */
const TOOLTIP_GAP = 6

/**
 * Combined outbound dashboard: the phone-line grid + map on top (per-line detail,
 * with a shared hover tooltip), then the allocation usage ribbon and the backlog
 * table below. All four regions are fed by one polled snapshot
 * (`useAllocationBoardQuery`), so they can never show different moments in time.
 */
export function PhoneLineBoardPage() {
  const isCareforceOrgAdmin = useIsCareforceOrgAdmin()
  const { data: board, isLoading, error } = useAllocationBoardQuery()
  const wrapRef = useRef<HTMLDivElement>(null)
  const [hover, setHover] = useState<HoverState | null>(null)

  // Stable duty → color map. Keyed on the two arrays it reads, NOT on `board`:
  // `generated_at` is a fresh timestamp every poll, so the top-level board object
  // always has a new identity and a [board] dep would rebuild this every 2s.
  // React Query's structural sharing does preserve the sub-arrays while their
  // contents are unchanged, so keyed this way the memo actually holds.
  const reservedByDuty = board?.reserved_by_duty
  const duties = board?.duties
  const colorMap = useMemo(
    () =>
      reservedByDuty && duties
        ? buildDutyColorMap({ reserved_by_duty: reservedByDuty, duties })
        : null,
    [reservedByDuty, duties]
  )

  // Position the shared tooltip relative to the wrapper from the hovered
  // element's rect (mouse-driven — no effect, no DOM query).
  const handleHover = useCallback((lineNumber: number, rect: DOMRect) => {
    const wrap = wrapRef.current
    if (!wrap) return
    const wr = wrap.getBoundingClientRect()
    const top = rect.top - wr.top + rect.height + TOOLTIP_GAP
    // Clamp inside the wrapper, reserving the tooltip's own width so it can't hang
    // off the right edge. Math.max last, so a wrapper narrower than the tooltip
    // pins it to the left edge instead of pushing it off-screen (the previous
    // order returned a negative left whenever wrap width < tooltip width).
    const left = Math.max(
      0,
      Math.min(rect.left - wr.left, wr.width - TOOLTIP_MIN_WIDTH)
    )
    setHover({ line: lineNumber, top, left })
  }, [])
  const handleLeave = useCallback(() => setHover(null), [])

  // Stable identity so the memoized ribbon/table aren't re-rendered by every mouse
  // move across the grid: hover state lives here, but neither of them reads it.
  // Declared above the early returns to keep hook order unconditional, hence the
  // optional call on a colorMap that is still null while loading.
  const colorFor = useCallback(
    (dutyId: string) => colorMap?.get(dutyId) ?? UNKNOWN_DUTY_COLOR,
    [colorMap]
  )

  // Must precede the loading branch: the query is disabled outside the Careforce
  // org, so `isLoading` is false and `board` is undefined — `!board` below would
  // otherwise catch this and spin forever.
  if (!isCareforceOrgAdmin) {
    return <CareforceOrgNotice />
  }

  if (error) {
    return (
      <div className="h-full overflow-auto px-6 py-4">
        {/* Fixed copy, not error.message. fetchJsonWithAuth surfaces whatever the
            server sent — helpcare's `detail`, or the relay's misconfiguration text
            which names RELAY_URL and X_CUSTOM_AUTH_SECRET — and none of that belongs
            on screen. The detail is still in the network tab and in hoku's logfire
            trace, which is where it's useful. The 2s poll keeps retrying, so this
            clears itself once the endpoint recovers. */}
        <Alert variant="destructive">
          <AlertDescription>{ALLOCATION_BOARD_ERROR}</AlertDescription>
        </Alert>
      </div>
    )
  }

  if (isLoading || !board || !colorMap) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    )
  }

  // Derived from Σ reserved_by_duty, not pool.reserved/pool.free — the same helper
  // the ribbon below uses, so the header and the bar can't tell different stories.
  const { total, held, free } = poolSplit(board)
  const cells = board.lines
  const hoveredLine = hover?.line ?? null
  // Look the line up by its number rather than assuming a dense 1..N index —
  // safer if the contract ever stops being contiguous (this is a PHI surface).
  const hoveredData =
    hoveredLine !== null
      ? (cells.find((l) => l.line_number === hoveredLine) ?? null)
      : null
  // Same defensive check as the map's dot loop: a null phone_line_id would throw
  // inside activeLinePlacement, and a hover shouldn't be able to crash the page.
  const hoveredCity =
    hoveredData?.status === 'active' && hoveredData.phone_line_id
      ? activeLinePlacement(hoveredData.phone_line_id).city
      : null

  return (
    <div className="h-full overflow-auto px-6 py-4">
      <div className="flex flex-col gap-4">
        {/* Top: line grid + map (per-line detail), with a shared hover tooltip.
            The grid card (2fr) drives the row height; the map (1fr) scales to it. */}
        <div
          ref={wrapRef}
          className="relative grid items-stretch gap-4 grid-cols-[2fr_1fr]">
          <Card className="rounded-lg p-3">
            {cells.length ? (
              <>
                <div className="mb-1.5 flex flex-wrap gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block size-2.5 rounded-[3px] bg-fuchsia-500 dark:bg-fuchsia-400" />
                    On a call
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block size-2.5 rounded-[3px] border border-slate-300 bg-slate-100 dark:border-slate-700 dark:bg-slate-800" />
                    Idle
                  </span>
                </div>
                <PhoneLineGrid
                  lines={cells}
                  hoveredLine={hoveredLine}
                  cols={ALLOCATION_GRID_COLS}
                  onHover={handleHover}
                  onLeave={handleLeave}
                />
              </>
            ) : (
              /* No lines at all — say so instead of rendering a legend over an
                 empty grid. The colour key is meaningless with nothing to key. */
              <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
                No lines in the pool.
              </div>
            )}
          </Card>

          <Card className="flex flex-col rounded-lg p-2.5">
            {/* The grid card is authoritative for the row height. The map is
                absolutely positioned so it contributes NO intrinsic height — it
                can't push the row taller; it just fills the box the grid's height
                leaves and scales its image to fit (letterboxed, centered).
                The min-height is the one exception: with an empty `lines[]` the
                grid collapses to nothing, and without a floor this box would be
                0px tall and the map would silently disappear. */}
            <div className="relative min-h-[200px] flex-1">
              <PhoneLineMap
                lines={cells}
                hoveredLine={hoveredLine}
                className="absolute inset-0 h-full w-full"
                onHover={handleHover}
                onLeave={handleLeave}
              />
            </div>
            <div className="mt-1.5 text-[11px] text-muted-foreground">
              Each dot is one active call. Locations are illustrative, not the
              caller's real location.
            </div>
          </Card>

          <PhoneLineTooltip
            line={hoveredData}
            city={hoveredCity}
            pos={hover ? { top: hover.top, left: hover.left } : null}
          />
        </div>

        {/* Bottom: allocation usage ribbon + backlog table. */}
        <Card className="rounded-lg p-4">
          <div className="mb-3 flex items-baseline justify-between gap-2">
            <h2 className="text-sm font-semibold text-foreground">
              Line pool — lines held by duty
            </h2>
            <span className="text-xs text-muted-foreground tabular-nums">
              {held.toLocaleString()} reserved · {free.toLocaleString()} free of{' '}
              {total.toLocaleString()}
            </span>
          </div>
          {/* Always render the ribbon — with nothing held it's a single 100% "free"
              segment, a clearer idle-pool view than a blank card. */}
          <AllocationRibbon board={board} colorFor={colorFor} />
        </Card>

        <Card className="rounded-lg p-4">
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            Backlog by duty
          </h2>
          {board.duties.length ? (
            <AllocationTable board={board} colorFor={colorFor} />
          ) : (
            <div className="text-xs text-muted-foreground">
              No duties with current demand.
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}
